import { CommonModule } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges } from "@angular/core";
import { isFunction, sortBy } from "lodash-es";
import { BaseComponent } from "../base/base.component";
import { getCanvasElement } from "@wm/core";

@Component({
    selector: 'wms-tree-node',
    standalone: true,
    templateUrl: './wms-tree-node.component.html',
    imports: [
        CommonModule
    ],
    host: { 'class': 'wms-tree-node' }
})

export class WmsTreeNodeComponent extends BaseComponent implements OnInit {
    @Input() dataItem: any;
    @Input() expandNode: boolean = false;
    @Input() level: any;
    @Input() id!: any;
    @Input() parentId!: any;
    @Input() searchText!: string;
    @Input() selectedNode!: any;
    @Input() getChildren!: any;
    @Input() sortBy!: any;
    @Input() hideChildren?: boolean;
    @Input() skipChildNodeSearch?: boolean;
    @Input() expandedNodes: any;
    @Input() expandOnClick?: boolean;
    @Input() enableDragDrop?: boolean;
    @Output() nodeClick = new EventEmitter<any>();
    @Output() onExpand = new EventEmitter<any>();

  @Output() nodeDragStart = new EventEmitter<any>();
  @Output() nodeDragEnd = new EventEmitter<any>();
  @Output() nodeDropped = new EventEmitter<any>();

  isDragging = false;
  isDragOver = false;
  dragPosition: 'prev' | 'after' | 'inside' | null = null;
  isDroppable:boolean = false;

    constructor(private elementRef: ElementRef) {
        super();
    }

    ngOnInit(): void {
        this.expandNode = this.searchText && !this.hideChildren && !this.skipChildNodeSearch ? this.hasChildrenWithSearchText(this.dataItem, this.searchText) : (this.expandNode || !this.dataItem?.collapsed);
        if (this.expandNode && this.dataItem?.hasChildren && !this.dataItem?.children?.length) {
          let children = this.getChildren(this.dataItem);
          if (this.isPromise(children)) {
            this.dataItem.children = children;
            return;
          }
          this.dataItem.children = children;
        }
        if (this.dataItem?.children && this.dataItem?.children?.length) {
          this.dataItem.children = sortBy(this.dataItem.children, this.sortBy);
        }
        if(this.expandNode && !this.dataItem.collapsed && isFunction(this.dataItem.children)){
          this.dataItem.children().then( (nodes: any) => this.sortAndExpand(nodes,true));
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        //to watch changes on label and badgeLabel - added for bind dialog
        if (this.selectedNode && this.dataItem?.uid && (this.selectedNode.dataItem?.uid === this.dataItem?.uid) && this.selectedNode.dataItem?.label && ((this.selectedNode.dataItem?.label !== this.dataItem.label) || (this.selectedNode.dataItem?.badgeLabel !== this.dataItem.badgeLabel))) {
            this.dataItem = { ...this.dataItem, label: this.selectedNode.dataItem.label, badgeLabel: this.selectedNode.dataItem.badgeLabel };
        }
    }

    toggleExpand() {
        // if the clicked node has the 'hasChildren' flag but the children property is null, fetch the child nodes using getChildren method
        if ((!this.dataItem.children || !this.dataItem.children.length) && this.dataItem.hasChildren) {
            this.dataItem.children = this.getChildren(this.dataItem)
        }

        if(this.isPromise(this.dataItem.children)){
            this.dataItem.children.then((children: any)=> this.sortAndExpand(children))
        } else if(isFunction(this.dataItem.children)) {
           this.dataItem.children().then( (nodes: any) => this.sortAndExpand(nodes,true));
        } else if (this.dataItem.children || this.dataItem.hasChildren) {
            this.sortAndExpand()
        }
    }

    sortAndExpand(children?: any, flag:boolean=false){
        this.dataItem.children = sortBy(children || this.dataItem.children, this.sortBy);
        if(flag) {
          this.expandNode = true;
        }else {
          this.expandNode = !(this.expandNode);
        }
        this.onExpand && this.onExpand.emit({id: this.dataItem.id, isExpanded: this.expandNode});
    }

    hasText(s1: string, s2: string): boolean {
        return s1.toLowerCase().includes(s2.toLowerCase());
    }

    // returns the display label for each dataItem
    getLabel(item: any) {
      if(!item) return '';
      return item.label || (item.idkey && this.locale[item.idkey]) || item.props?.name || item.id;
    }

    /**
     * This method checks till the deepest level of a tree node and returns a boolean if it has children that match the search keyword
     * @param node Tree node
     * @param searchTerm search keyword
     * @returns boolean
     */
    hasChildrenWithSearchText(node: any, searchTerm: string): boolean {
        let hasMatchedChildren = false;
        if (!node?.children?.length) {
            return hasMatchedChildren
        }
        let childHasMatchedChildren;
        node.children.forEach((childNode: any) => {
            childHasMatchedChildren = childNode.children?.length ? this.hasChildrenWithSearchText(childNode, searchTerm) : this.hasText(childNode.id, searchTerm);
            if (hasMatchedChildren) return;
        })

        hasMatchedChildren = node.children.some((childNode: any) => this.hasText(childNode.id, searchTerm)) || childHasMatchedChildren;
        return hasMatchedChildren;
    }

    handleNodeClick(event: Event) {
      let children;
      // if the clicked node has the 'hasChildren' flag but the children property is null, fetch the child nodes using getChildren method
      if (!this.dataItem.children && this.dataItem.hasChildren) {
          children = this.getChildren(this.dataItem)
      }
      if (this.isPromise(children)) {
        children.then((response: any) => {
          this.dataItem.children = response;
          this.nodeClick.emit({ dataItem: this.dataItem, id: this.id, event: event })
          if(this.expandOnClick) this.toggleExpand()
        })
      } else {
        this.nodeClick.emit({ dataItem: this.dataItem, id: this.id, event: event })
        if(this.expandOnClick) this.toggleExpand()
      }
    }

    isPromise(data: Promise<any>) {
        if (data instanceof Promise) {
            return true;
        }
        return false;
    }

    canExpandNode(nodeId: any): boolean {
        return nodeId && this.expandedNodes && this.expandedNodes.length && this.expandedNodes.includes(nodeId);
    }

    getRelativePath(item: any) {
      //we are setting this info in tree item node to get the relative path of uploaded resource in file workspace
      if(item && typeof item === "object" && item.hasOwnProperty('type') && item.hasOwnProperty('relativePath')) {
        return JSON.stringify({
          type: item.type,
          relativePath: item.relativePath
        });
      }
      return '';
    }
    hasLazyChildren(fn: any) {
      return isFunction(fn) || fn && fn.__zone_symbol__value?.length;
    }

    // Enhanced Drag & Drop Methods
  onDragStart(event: DragEvent, position?: string): void {
    if (!event.dataTransfer) return;

    this.isDragging = true;
    event.dataTransfer.effectAllowed = 'move';

      // Prevent default ghost image (drag clone)
    const emptyDragImage = document.createElement('div');
    emptyDragImage.style.width = '0px';
    emptyDragImage.style.height = '0px';
    event.dataTransfer.setDragImage(emptyDragImage, 0, 0);
    
    const dragData = {
      nodeId: this.dataItem.id || this.id,
      parentId: this.parentId,
      level: this.level,
      dataItem: this.dataItem
    };

     // Add source identifier
    event.dataTransfer.setData('application/x-tree-node', 'true');
    event.dataTransfer.setData('text/plain', JSON.stringify(dragData));

    this.nodeDragStart.emit({
      dragData,
      event,
      sourceNode: this.dataItem,
      sourceParent: this.parentId
    });
  }

  onDragEnd(event: DragEvent): void {
    this.clearDragState();
    this.isDragging = false;
    this.nodeDragEnd.emit({
      event,
      sourceNode: this.dataItem
    });
  }

  onDragOver(event: DragEvent, position?: 'prev' | 'after' | 'inside'): void {
    event.preventDefault();

    // Check if this is a tree node drag
    const isTreeDrag = event.dataTransfer?.types.includes('application/x-tree-node');
    if (!isTreeDrag) {
      // This is a canvas drag - don't show tree indicators
      return;
    }
    event.stopPropagation();

    try {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();

      this.isDragOver = true;
      this.dragPosition = position ?? this.calculateDropPosition(event);

      if (this.dragPosition === 'inside') {
        this.isDroppable = this.shouldBlockDrop(this.dataItem.props.widgetId)
      } 

      const paddingLeft = 5 + (20 * (this.level - 1))
      const width = rect.width - paddingLeft
      // Update visual feedback
      this.updateDropZoneVisuals(this.dragPosition, width);

    } catch (e) {
      console.warn('Invalid drag data');
    }
  }

  private calculateDropPosition(event: DragEvent): 'prev' | 'after' | 'inside' {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const offsetY = event.clientY - rect.top;
    if (offsetY < rect.height * 0.30) {
      return 'prev';
    } else if (offsetY > rect.height * 0.60) {
      return 'after';
    } else {
      return 'inside';
    }
  }


  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.dragPosition = null;
    
    // Check if we're actually leaving the node
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const isOutside = event.clientX < rect.left || 
                     event.clientX > rect.right || 
                     event.clientY < rect.top || 
                     event.clientY > rect.bottom;

    if (isOutside) {
      this.clearDragState();
    }
  }

  onDrop(event: DragEvent, explicitPosition?: 'prev' | 'after' | 'inside'): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (!event.dataTransfer) return;

    const position = explicitPosition ?? this.dragPosition;
    const dragDataStr = event.dataTransfer.getData('text/plain');
    if (!dragDataStr) return;

    try {
      const dragData = JSON.parse(dragDataStr);

      // Validate drop
      if (this.isInvalidDrop(dragData, position)) {
        this.clearDragState();
        return;
      }
      
      // Clear visual state
      this.clearDragState();

      // Emit drop event
      this.nodeDropped.emit({
        dragData,
        targetNode: this.dataItem,
        targetParent: this.parentId,
        position,
        event
      });

    } catch (e) {
      console.warn('Drop failed: Invalid data');
      this.clearDragState();
    }
  }

  private isInvalidDrop(dragData: any, position: any): boolean {
    // Prevent dropping on self
    if (dragData.nodeId === (this.dataItem.id || this.id)) {
      return true;
    }
    if (position === 'inside' && !this.shouldBlockDrop(this.dataItem.props.widgetId)) {
      return true
    }
    return this.isDescendant(dragData);
  }

  private isDescendant(dragData: any): boolean { 
    if (!dragData.dataItem.children) return false; 
    const checkDescendants = (children: any[]): boolean => { 
      for (const child of children) { 
        if (child.id === this.dataItem.id) return true;
        if (child.children && checkDescendants(child.children)) return true; 
      }
       return false;
     }; 
     return checkDescendants(dragData.dataItem.children); }

  shouldBlockDrop(widgetId: any) {
    let canvasEl = getCanvasElement();
    if (!canvasEl) return false;

    const element = canvasEl.querySelector('[widget-id="' + widgetId + '"]');
    if (!element) return false;
    // Don't drop inside partial container
    if(element.getAttribute('is-container') && element.hasAttribute('content')){
      return false;
    }
    const isDroppable = element.getAttribute('wm-droppable') 
    return isDroppable
  }

  private updateDropZoneVisuals(position:any, width: any): void {
    const element = this.elementRef.nativeElement;
    
    const activeParent  = element.parentElement?.querySelector('.tree-node');

    const allNodes = document.querySelectorAll('.drag-over-parent');
    allNodes.forEach(node => {
      if(node != activeParent) {
        node.classList.remove('drag-over-parent')
      }
    });

    if (activeParent) {
      if (position !== 'inside') {
        if (!activeParent.classList.contains('drag-over-parent')) {
          activeParent.classList.add('drag-over-parent');
        }
        activeParent.classList.remove('drag-over-after', 'drag-over-prev', 'drag-over-inside');
      } else {
        if (!this.isDroppable) {
          activeParent.classList.add('drag-over-parent');
        } else {
          activeParent.classList.remove('drag-over-parent');
        }
      }
    }

    element.classList.remove('drag-over-prev', 'drag-over-after', 'drag-over-inside', 'drag-over-parent');
    element.style.setProperty('--prev-width', `${width}px`);
  }

  private clearDragState(): void {
    this.isDragOver = false;
    this.dragPosition = null;
    this.clearAllDragVisuals()
  }

   trackById(index: number, node: any) {
    return node.id;
  }

  clearAllDragVisuals(){
    const allNodes = document.querySelectorAll('.tree-node');
    allNodes.forEach(node => {
        node.classList.remove('drag-over-before', 'drag-over-after', 'drag-over-inside', 'drag-over-parent', 'drag-over');
    });
  }

}
